import { Dialog } from '@headlessui/react';
import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Button, Input } from '../ui';

interface StageDateModalProps {
  isOpen: boolean;
  companyName: string;
  stageName: string;
  isSaving: boolean;
  /** `when` is now for today, or noon local on the chosen day. */
  onConfirm: (when: Date) => void;
  onCancel: () => void;
}

const localYmd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const labelFor = (ymd: string) =>
  new Date(`${ymd}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

/**
 * Asked after a card lands in a new stage: when did that actually happen?
 * Defaults to today so the common case is one click (or Enter). Picking an
 * earlier day backdates stage_entered_at and, on first entry, the Google Ads
 * stamps (qualified_at / closed_won_at / closed_lost_at).
 */
export default function StageDateModal({ isOpen, companyName, stageName, isSaving, onConfirm, onCancel }: StageDateModalProps) {
  const today = localYmd(new Date());
  const [ymd, setYmd] = useState(today);

  useEffect(() => {
    if (isOpen) setYmd(localYmd(new Date()));
  }, [isOpen]);

  const isToday = ymd === today;
  const confirm = () => {
    if (isSaving || !ymd) return;
    onConfirm(isToday ? new Date() : new Date(`${ymd}T12:00:00`));
  };

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        confirm();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  return (
    <Dialog open={isOpen} onClose={() => !isSaving && onCancel()} className="relative" style={{ zIndex: 50 }}>
      <div className="fixed inset-0" style={{ background: 'var(--surface-overlay)' }} aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4 ck-safe-pad">
        <Dialog.Panel
          className="mx-auto max-w-md w-full"
          style={{
            background: 'var(--surface-card)',
            border: '1px solid var(--border-hairline)',
            borderRadius: 'var(--radius-xl)',
            boxShadow: 'var(--shadow-pop)',
            padding: 'var(--space-6)',
          }}
        >
          <Dialog.Title
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '11px',
              margin: '0 0 12px',
              fontSize: 'var(--text-md)',
              fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
              letterSpacing: 'var(--tracking-tight)',
              color: 'var(--text-body)',
            }}
          >
            <FontAwesomeIcon icon={faCalendarDays} style={{ color: 'var(--accent-text)', fontSize: '15px' }} />
            Moved to {stageName}
          </Dialog.Title>

          <p style={{ margin: '0 0 16px', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 1.5 }}>
            When did {companyName} reach {stageName}? Leave it on today, or pick the day it actually happened.
          </p>

          <Input
            label="Date"
            type="date"
            value={ymd}
            max={today}
            onChange={(e) => setYmd(e.target.value)}
            style={{ marginBottom: '22px' }}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <Button variant="ghost" onClick={onCancel} disabled={isSaving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={confirm} disabled={isSaving || !ymd}>
              <FontAwesomeIcon
                icon={isSaving ? faSpinner : faCalendarDays}
                className={isSaving ? 'animate-spin' : undefined}
                style={{ fontSize: '12px', marginRight: '7px' }}
              />
              {isSaving ? 'Saving...' : isToday ? 'Today' : `Set ${labelFor(ymd)}`}
            </Button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
